import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { SortByOptions } from "@/components/review-modal/types";
import apollo from "@/helpers/apollo";
import { gameUrl } from "@/helpers/games";
import type { DataType } from "@/pages/game";
import Game from "@/pages/game";
import { QUERY } from "@/pages/game/graphql";
import { getGameMetaProps } from "@/pages/game/helpers/get-meta-props";
import {
  getGameSchema,
  organizationSchema,
} from "@/pages/game/helpers/seo-schemas";

interface AttractionVariables {
  id: string;
  includeReview: boolean;
  page?: number;
  pageSize?: number;
  reviewId: string;
  sortBy?: (typeof SortByOptions)[keyof typeof SortByOptions];
}

const fetchAttraction = createServerFn({ method: "GET" })
  .inputValidator((data: { splat: string }) => data)
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: URL segment parsing mirrors existing getServerSideProps logic
  .handler(async ({ data }) => {
    const segments = data.splat.split("/").filter(Boolean);
    const [id, slug, ...rest] = segments;

    if (!id || Number.isNaN(Number(id))) {
      throw notFound();
    }

    const variables: AttractionVariables = {
      id,
      includeReview: false,
      pageSize: 10,
      reviewId: "0",
    };
    const path: string[] = ["attraction", id];

    if (slug) {
      path.push(slug);
    }

    for (const [index, segment] of rest.entries()) {
      if (segment.startsWith("reviews:page:")) {
        const page = Math.max(1, Number.parseInt(segment.slice(13), 10));
        variables.page = page;
        if (page > 1) {
          path.push(segment);
        }
      }
      if (segment.startsWith("reviews:sort:")) {
        const sort = segment.slice(13);
        if (sort in SortByOptions) {
          variables.sortBy = SortByOptions[sort as keyof typeof SortByOptions];
          path.push(segment);
        }
      }
      if (segment === "review" && rest[index + 1] !== undefined) {
        variables.includeReview = true;
        variables.reviewId = rest[index + 1];
      }
    }

    const client = apollo();
    const response = await client.query<DataType, AttractionVariables>({
      fetchPolicy: "no-cache",
      query: QUERY,
      variables,
    });
    const responseData = response.data;

    if (!responseData) {
      throw notFound();
    }

    if (responseData.game.slug && slug !== responseData.game.slug) {
      let destination = gameUrl(responseData.game);

      if (rest.length) {
        destination += `/${rest.join("/")}`;
      }

      throw redirect({ statusCode: 301, to: destination });
    }

    if (
      responseData.review &&
      responseData.review.escape.game?.id !== responseData.game.id
    ) {
      throw notFound();
    }

    return { ...responseData, path };
  });

export const Route = createFileRoute("/attraction/$")({
  component: AttractionPage,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {};
    }
    const { description, image, title, url } = getGameMetaProps(
      loaderData.game
    );
    const gameSchema = getGameSchema(
      loaderData.game,
      loaderData.reviews.count || 0,
      loaderData.reviews.objects
    );
    const isReviewPage = loaderData.path.includes("review");

    return {
      links: [{ href: url, rel: "canonical" }],
      meta: [
        { title },
        { content: description, name: "description" },
        { content: description, property: "og:description" },
        { content: image, property: "og:image" },
        { content: title, property: "og:title" },
        { content: "website", property: "og:type" },
        { content: url, property: "og:url" },
        { content: "summary_large_image", name: "twitter:card" },
        { content: "@mortyapp", name: "twitter:site" },
        ...(isReviewPage ? [{ content: "noindex", name: "robots" }] : []),
        { "script:ld+json": organizationSchema() },
        { "script:ld+json": gameSchema },
      ],
    };
  },
  loader: ({ params }) =>
    fetchAttraction({ data: { splat: params._splat || "" } }),
});

function AttractionPage() {
  const data = Route.useLoaderData();
  return <Game {...data} />;
}
